import { I18n } from '@calendly/i18n'
import { GridItem } from '@calendly/ui/components/grid'
import { IconButton } from '@calendly/ui/components/icon-button'
import { BodyText } from '@calendly/ui/components/typography'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import { verifyDomainControl } from '../actions'
import { DELETE_DOMAIN_POPUP, DOMAIN_STATES, VERIFICATION_METHODS } from '../constants'
import { domainErrorSelector, expandedDomainCardsSelector, loadingVerificationsSelector } from '../selectors'
import { setExpandedCardDomain, setDomainForPopup, openPopup } from '../slice'
import { canTryVerifyByTimeframe } from '../utils'
import {
  ArrowIcon,
  Card,
  CardHeader,
  CloseCircleIcon,
  DomainCardBody,
  DomainCardErrorStatus,
  DomainCardStatus,
  DomainCardStatuses,
  DomainCardTitle,
  DomainCardTitleButton,
  ErrorExclamationCircle,
  InterfaceTimeThreeIcon,
  SuccessIcon,
  TrashIcon
} from './shared_styles'
import { VerificationCName } from './verification_cname'
import { VerificationTxt } from './verification_txt'
import { CardVerified } from './verified'

export const DomainCard = ({ domain }) => {
  const dispatch = useDispatch()
  const expandedDomains = useSelector(expandedDomainCardsSelector)
  const loadingVerifications = useSelector(loadingVerificationsSelector)
  const error = useSelector(domainErrorSelector(domain.uuid))
  const isExpanded = expandedDomains[domain.uuid] || false
  const verifyLoading = loadingVerifications[domain.uuid] || false
  const canAttemptVerifyByTimeframe = canTryVerifyByTimeframe(domain.last_checked_at)
  const canAttemptVerifyByState = domain.state === DOMAIN_STATES.PENDING

  const handleExpandClick = () => {
    dispatch(setExpandedCardDomain({ uuid: domain.uuid, value: !isExpanded }))
  }

  const handleVerifyClick = () => {
    dispatch(verifyDomainControl(domain))
  }

  const handleDeleteDomain = () => {
    dispatch(setDomainForPopup(domain))
    dispatch(openPopup(DELETE_DOMAIN_POPUP))
  }

  const statusItem = () => {
    if (domain.state === DOMAIN_STATES.CAPTURED_BY_OTHER_ORG) {
      return (
        <DomainCardStatus>
          <CloseCircleIcon />
          <DomainCardErrorStatus>
            {I18n.t('publisher.organization_settings.domain_control.cards.statuses.captured_by_other_org')}
          </DomainCardErrorStatus>
        </DomainCardStatus>
      )
    }
    if (domain.state === DOMAIN_STATES.VERIFIED) {
      return (
        <>
          <DomainCardStatus>
            <SuccessIcon />
            <BodyText inline>{I18n.t('publisher.organization_settings.domain_control.cards.statuses.verified')}</BodyText>
          </DomainCardStatus>
          {domain.enforced ? (
            <DomainCardStatus>
              <SuccessIcon />
              <BodyText inline>{I18n.t('publisher.organization_settings.domain_control.cards.statuses.enabled')}</BodyText>
            </DomainCardStatus>
          ) : (
            <DomainCardStatus>
              <CloseCircleIcon />
              <BodyText inline>{I18n.t('publisher.organization_settings.domain_control.cards.statuses.not_enabled')}</BodyText>
            </DomainCardStatus>
          )}

        </>
      )
    }
    if (domain.last_checked_at || error != null) {
      return (
        <DomainCardStatus>
          <ErrorExclamationCircle />
          <DomainCardErrorStatus>
            {I18n.t('publisher.organization_settings.domain_control.cards.statuses.unable_verify')}
          </DomainCardErrorStatus>
        </DomainCardStatus>
      )
    }
    return (
      <DomainCardStatus>
        <InterfaceTimeThreeIcon />
        <BodyText inline>
          {I18n.t('publisher.organization_settings.domain_control.cards.statuses.pending')}
        </BodyText>
      </DomainCardStatus>
    )
  }

  const cardBody = () => {
    if (DOMAIN_STATES.VERIFIED === domain.state) {
      return <CardVerified domain={domain} />
    }

    const componentProps = {
      domain,
      onVerifyClick: handleVerifyClick,
      verifyLoading,
      error,
      canAttemptVerifyByTimeframe,
      canAttemptVerifyByState
    }

    switch (domain.verification_method) {
      case VERIFICATION_METHODS.DNS_CNAME:
        return (<VerificationCName {...componentProps} />)
      case VERIFICATION_METHODS.DNS_TXT:
        return (<VerificationTxt {...componentProps} />)
      case VERIFICATION_METHODS.MANUAL:
      default:
        return null
    }
  }

  return (
    <Card data-component="domain-control-card">
      <CardHeader>
        <GridItem size="6" sizeMobile="12">
          <DomainCardTitleButton onClick={handleExpandClick}>
            <ArrowIcon isExpanded={isExpanded} />
            <DomainCardTitle>{domain.domain}</DomainCardTitle>
          </DomainCardTitleButton>
        </GridItem>
        <GridItem size="5">
          <DomainCardStatuses>{statusItem()}</DomainCardStatuses>
        </GridItem>
        <GridItem size="1">
          <IconButton onClick={handleDeleteDomain} title={I18n.t('publisher.action.delete')}>
            <TrashIcon />
          </IconButton>
        </GridItem>
      </CardHeader>
      {isExpanded && (
      <DomainCardBody>
        {cardBody()}
      </DomainCardBody>
      )}
    </Card>
  )
}

DomainCard.propTypes = {
  domain: PropTypes.shape({
    domain: PropTypes.string.isRequired,
    enforced: PropTypes.bool,
    last_checked_at: PropTypes.string,
    state: PropTypes.string,
    uuid: PropTypes.string.isRequired,
    verification_method: PropTypes.string.isRequired,
    verification_code: PropTypes.string,
  }).isRequired,
}
